import type { ComponentType } from "react";
import type { UserOut } from "@/api/client";
import { isToolsOnly, TOOLS_HOME } from "@/auth/role";
import type { IconName } from "@/components/Icon";

const SETTINGS_PATH = "/settings";

export type NavItem = {
  label: string;
  path: string;
  icon: IconName;
  ready: boolean;
  // Страница — отдельный кусок сборки, грузится при первом заходе на неё
  // (router.tsx). Сайдбару нужны только подписи и пути.
  page: () => Promise<ComponentType>;
};

export type NavSection = {
  title: string;
  items: NavItem[];
};

// Sidebar model. Sections/items are added back one page at a time as they are
// built (flip `ready` + wire `page`).
export const NAV: NavSection[] = [
  {
    title: "Подбор",
    items: [
      {
        label: "Вакансии",
        path: "/vacancies",
        icon: "briefcase",
        ready: true,
        page: () => import("@/pages/Vacancies/VacanciesPage").then((m) => m.VacanciesPage),
      },
      {
        label: "Кандидаты",
        path: "/candidates",
        // Одна фигурка, как в эталоне; две — у «Дубликаты».
        icon: "user",
        ready: true,
        page: () => import("@/pages/Candidates/CandidatesPage").then((m) => m.CandidatesPage),
      },
      {
        // Поиск под вакансию: без вакансии в адресе — её выбор, выдача — на
        // /search/:ref (маршрут в router.tsx, страница та же).
        label: "Поиск",
        path: "/search",
        icon: "search",
        ready: true,
        page: () => import("@/pages/Search/SearchPage").then((m) => m.SearchPage),
      },
      {
        label: "Трафик",
        path: "/traffic",
        icon: "traffic",
        ready: true,
        page: () => import("@/pages/Traffic/TrafficPage").then((m) => m.TrafficPage),
      },
      {
        label: "Чаты",
        path: "/chats",
        icon: "message",
        ready: true,
        page: () => import("@/pages/Chats/ChatsPage").then((m) => m.ChatsPage),
      },
      {
        label: "Интервью",
        path: "/interviews",
        icon: "video",
        ready: true,
        page: () => import("@/pages/Interviews/InterviewsPage").then((m) => m.InterviewsPage),
      },
      {
        // База всех людей, не кандидатур. Карточка человека — окно поверх списка
        // (маршрут /people/:id, router.tsx), как страница вакансии.
        label: "Люди",
        path: "/people",
        icon: "users",
        ready: true,
        page: () => import("@/pages/People/PeoplePage").then((m) => m.PeoplePage),
      },
    ],
  },
  {
    title: "Бизнес",
    items: [
      {
        label: "Клиенты",
        path: "/clients",
        icon: "building",
        ready: true,
        page: () => import("@/pages/Clients/ClientsPage").then((m) => m.ClientsPage),
      },
      {
        label: "Документы",
        path: "/documents",
        icon: "file-text",
        ready: true,
        page: () => import("@/pages/Documents/DocumentsPage").then((m) => m.DocumentsPage),
      },
      {
        // Обычный пункт меню, как в эталоне (раньше — шестерёнка в подвале).
        // Значок — солнце с лучами. «Шаблоны» — вкладка этой страницы
        // (/templates ведёт на неё, router.tsx).
        label: "Настройки",
        path: SETTINGS_PATH,
        icon: "settings",
        ready: true,
        page: () => import("@/pages/Settings/SettingsPage").then((m) => m.SettingsPage),
      },
      {
        // Расширение для браузера: установлено ли, скачать zip (кладёт CI,
        // deploy:newats) и общие для компании переключатели. После
        // «Настроек», значок пазла — как в эталоне. Не /extension: там, в
        // nginx, лежат сам zip и version.json.
        label: "Расширение",
        path: "/browser-extension",
        icon: "puzzle",
        ready: true,
        page: () => import("@/pages/Extension/ExtensionPage").then((m) => m.ExtensionPage),
      },
    ],
  },
  {
    title: "Прочее",
    items: [
      {
        label: "Дубликаты",
        path: "/duplicates",
        icon: "users",
        ready: true,
        page: () => import("@/pages/Duplicates/DuplicatesPage").then((m) => m.DuplicatesPage),
      },
      {
        label: "Инструменты",
        path: "/tools",
        icon: "automation",
        ready: true,
        page: () => import("@/pages/Tools/ToolsPage").then((m) => m.ToolsPage),
      },
    ],
  },
];

/** Разделы, которые видит этот пользователь. У роли «Инструменты» — её страница
 *  и свои настройки (раньше шли шестерёнкой в подвале, теперь это пункт меню):
 *  остальное сервер ей всё равно не отдаст (ats_api.auth), и пункты меню,
 *  ведущие в 403, выглядели бы поломкой, а не запретом. */
export function navFor(user: UserOut | null | undefined): NavSection[] {
  if (!isToolsOnly(user)) return NAV;
  const all = NAV.flatMap((s) => s.items);
  const items = [TOOLS_HOME, SETTINGS_PATH]
    .map((path) => all.find((i) => i.path === path))
    .filter((i): i is NavItem => !!i);
  return items.length ? [{ title: "", items }] : [];
}
